import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField
} from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { useClasses } from 'hooks/useClasses';
import { useSessions, SessionData } from 'hooks/useSession';
import { useTranslation } from 'react-i18next';
import { useError } from 'contexts/ErrorContext';
import { useCurrentStudent } from 'hooks/useCurrentStudent';
import { useStudents } from 'hooks/useStudents';
import { useSessionClass } from 'contexts/SessionClassContext';
import { getClassRank } from 'components/enum/ClassOptions';

interface StudentPromotionFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
  initialData?: {
    id?: number;
    studentId: number;
    studentName: string;
    classId: number;
    sessionId: number;
    schoolType: string;
  };
}


const StudentPromotionFormModal: React.FC<StudentPromotionFormModalProps> = ({
  open,
  handleClose,
  onSuccess,
  initialData
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showSuccess, showError } = useError();
  const { selectedClass: layoutSelectedClass } = useSessionClass();

  const schoolId = user?.schoolId ?? undefined;

  const { classesQuery } = useClasses(schoolId);
  const { sessionsQuery } = useSessions(schoolId);
  const { studentData } = useCurrentStudent();
  const { promoteRequestMutation, updatePromoteRequestMutation } =
    useStudents(false, schoolId);

  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
  const [selectedSessionId, setSelectedSessionId] = useState<number | ''>('');
  const [schoolType, setSchoolType] = useState('SAME');

  /** ----------------------------------
   * LAYOUT SELECTED CLASS (from context)
   * This is the class selected in the layout dropdown
   * ---------------------------------- */
  const layoutClass = useMemo(() => {
    if (!layoutSelectedClass || !classesQuery.data) return null;
    return classesQuery.data.find(c => c.id === layoutSelectedClass.id) || null;
  }, [layoutSelectedClass, classesQuery.data]);

  /** ----------------------------------
   * NEXT CLASS OF LAYOUT SELECTED CLASS
   * ---------------------------------- */
  const nextClassOfLayout = useMemo(() => {
    if (!classesQuery.data || !layoutClass) return null;

    const nextClassRank = getClassRank(layoutClass.name) + 1;

    return classesQuery.data.find(
      c =>
        getClassRank(c.name) === nextClassRank &&
        c.section === layoutClass.section
    ) || null;
  }, [classesQuery.data, layoutClass]);

  const eligibleClasses = useMemo(() => {
    if (!classesQuery.data || !layoutClass) return [];

    const allClasses = classesQuery.data;
    const currentClassRank = getClassRank(layoutClass.name);
    const nextClassRank = currentClassRank + 1;

    const list: any[] = [];

    // All sections of CURRENT class (e.g., 10th A, 10th C)
    const currentClassSections = allClasses.filter(
      c => getClassRank(c.name) === currentClassRank
    );
    list.push(...currentClassSections);

    // All sections of NEXT class (e.g., 11th A, 11th B)
    const nextClassSections = allClasses.filter(
      c => getClassRank(c.name) === nextClassRank
    );
    list.push(...nextClassSections);

    // Always include the currently selected class in modal if it's not already in the list
    if (selectedClassId) {
      const selectedClass = allClasses.find(c => c.id === selectedClassId);
      if (selectedClass && !list.some(c => c.id === selectedClass.id)) {
        list.push(selectedClass);
      }
    }

    return list;
  }, [layoutClass, classesQuery.data, selectedClassId]);

  /** ----------------------------------
   * DEFAULT CLASS SELECTION
   * ---------------------------------- */
  useEffect(() => {
    if (open) {
      if (initialData?.classId) {
        // Pre-fill from initialData when editing
        setSelectedClassId(initialData.classId);
      } else if (layoutClass && layoutClass.id) {
        // Use layout selected class when creating
        setSelectedClassId(layoutClass.id);
      }
    }
  }, [layoutClass, initialData?.classId, open]);

  /** ----------------------------------
   * NEXT SESSION LOGIC
   * ---------------------------------- */
  const currentSessionName = studentData?.sessionName || '';

  let nextSession: SessionData | undefined;
  if (sessionsQuery.data && currentSessionName) {
    const sorted = [...sessionsQuery.data].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const idx = sorted.findIndex(s => s.name === currentSessionName);
    if (idx !== -1 && idx < sorted.length - 1) {
      nextSession = sorted[idx + 1];
    }
  }

  useEffect(() => {
    if (open) {
      if (initialData?.sessionId) {
        // Pre-fill from initialData when editing
        setSelectedSessionId(initialData.sessionId);
      } else if (nextSession && nextSession.id) {
        // Use next session when creating
        setSelectedSessionId(nextSession.id);
      }
      // Pre-fill schoolType from initialData if available
      if (initialData?.schoolType) {
        setSchoolType(initialData.schoolType);
      }
    }
  }, [nextSession, initialData, open]);

  /** ----------------------------------
   * SUBMIT HANDLER
   * ---------------------------------- */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClassId || !selectedSessionId) return;

    const studentId = initialData?.studentId ?? studentData?.id;
    const studentName = initialData?.studentName ?? studentData?.studentName;

    if (!studentId || !studentName) {
      showError(t('student_information_missing') || 'Student information is missing');
      return;
    }

    const payload = {
      studentId,
      studentName,
      classId: Number(selectedClassId),
      sessionId: Number(selectedSessionId),
      schoolType,
      prevClass: layoutSelectedClass?.id // Current selected class in layout
    };

    if (initialData?.id) {
      updatePromoteRequestMutation.mutate(
        { id: initialData.id, dto: payload },
        {
          onSuccess: () => {
            showSuccess(
              t('promotion_request_updated_successfully') || 'Promotion request updated successfully'
            );
            onSuccess?.();
            handleClose();
          },
          onError: (err: any) => {
            showError(
              err?.response?.data?.message ||
                t('error_updating_promotion_request') ||
                t('something_went_wrong') ||
                'Something went wrong'
            );
          }
        }
      );
    } else {
      promoteRequestMutation.mutate(
        payload,
        {
          onSuccess: () => {
            showSuccess(
              t('promotion_request_submitted_successfully') || 'Promotion request submitted successfully'
            );
            onSuccess?.();
            handleClose();
          },
          onError: (err: any) => {
            showError(
              err?.response?.data?.message ||
                t('error_submitting_promotion_request') ||
                t('something_went_wrong') ||
                'Something went wrong'
            );
          }
        }
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('promotion_form')}</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            value={
              initialData?.studentName ??
              studentData?.studentName ??
              ''
            }
            label={t('student_name')}
            disabled
            fullWidth
            margin="normal"
          />

          {/* PROMOTION CLASS */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('promotion_class')}</InputLabel>
            <Select
              value={selectedClassId}
              label={t('promotion_class')}
              onChange={e => setSelectedClassId(Number(e.target.value))}
            >
              {eligibleClasses.map(cls => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name} {cls.section}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* SESSION (READ ONLY) */}
          <TextField
            value={
              sessionsQuery.data?.find(
                s => s.id === selectedSessionId
              )?.name || ''
            }
            label={t('promotion_session')}
            disabled
            fullWidth
            margin="normal"
          />

          {/* SCHOOL TYPE */}
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('school_type')}</InputLabel>
            <Select
              value={schoolType}
              label={t('school_type')}
              onChange={e => setSchoolType(e.target.value)}
            >
              <MenuItem value="SAME">{t('same')}</MenuItem>
              <MenuItem value="OTHER">{t('other')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              promoteRequestMutation.isLoading ||
              updatePromoteRequestMutation.isLoading ||
              !selectedClassId ||
              !selectedSessionId
            }
          >
            {promoteRequestMutation.isLoading || updatePromoteRequestMutation.isLoading ? (
              <CircularProgress size={22} />
            ) : initialData?.id ? (
              t('update')
            ) : (
              t('submit')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default StudentPromotionFormModal;
